import { useRef } from "react";
import { IVehicleDetailed } from "@/interfaces/IVehicleDetailed";
import { Helmet } from "react-helmet";
import { getBackend } from "@api";
import useStore from "@store";
import Headline from "./Headline";

function shortenDescription(description: string, maxLength = 160): string {
  if (!description) return "";
  if (description.length <= maxLength) return description;
  let truncatedDescription = description.substring(0, maxLength);
  const lastSpaceIndex = truncatedDescription.lastIndexOf(" ");
  if (lastSpaceIndex > -1) {
    truncatedDescription = truncatedDescription.substring(0, lastSpaceIndex);
  }
  return truncatedDescription + "...";
}

interface IMetaTags {
  canonicalUrl: string;
  metaTitle?: string;
  metaDescription?: string;
  h1?: string;
  h2?: string;
}

const MetaTags: React.FC<IMetaTags> = ({ canonicalUrl, metaTitle, metaDescription, h1, h2 }) => {
  // For Vehicle Detail Page
  const { currentVehicleInDetail } = useStore();
  const vehicle = currentVehicleInDetail || ({} as IVehicleDetailed);

  const isRoot = window.location.pathname === "/";
  if (isRoot) return null;

  const { customerConfiguration } = useStore();
  const hasCleaned = useRef(false);

  // Meta Data
  const title = metaTitle || vehicle?.seo?.t;
  const description = metaDescription || vehicle?.seo?.d || shortenDescription(vehicle?.desc || "");
  const name = [vehicle?.ma, vehicle?.mo, vehicle?.na].filter(Boolean).join(" ");
  const url = canonicalUrl;
  const image = vehicle?.mm?.u?.[0] ? getBackend(vehicle?.mm?.u?.[0]) : undefined;
  const author = vehicle?.Branch?.Alias || customerConfiguration?.name;
  const richSnippet = vehicle?.seo?.so;

  // Remove existing meta data, because helmet not override them in snippet context
  if (!hasCleaned.current) {
    hasCleaned.current = true;
    const selectors = [title && "title", description && "meta[name='description']", author && "meta[name='author']", url && "link[rel='canonical']", url && "meta[property='og:url']", url && "meta[name='twitter:url']", image && "meta[property='og:image']", image && "meta[name='twitter:image']"].filter(Boolean);

    selectors.forEach((selector) => {
      const elements = document.querySelectorAll(selector!);
      elements.forEach((el) => el.remove());
    });
  }

  return (
    <>
      {h1 && <Headline title={h1} subtitle={h2 || ""} />}
      <Helmet>
        {title && <title>{title}</title>}
        {description && <meta name="description" content={description} />}
        {author && <meta name="author" content={author} />}
        {url && <link rel="canonical" href={url} />}
        {url && <meta property="og:url" content={url} />}
        {url && <meta name="twitter:url" content={url} />}
        <meta property="og:type" content="website" />
        {name && <meta property="og:title" content={name} />}
        {description && <meta property="og:description" content={description} />}
        {image && <meta property="og:image" content={image} />}
        {name && <meta property="og:image:alt" content={`Image of ${name}`} />}
        <meta name="twitter:card" content="summary_large_image" />
        {name && <meta name="twitter:title" content={name} />}
        {description && <meta name="twitter:description" content={description} />}
        {image && <meta name="twitter:image" content={image} />}
      </Helmet>
      {richSnippet && addSchemaOrg(richSnippet)}
    </>
  );
};

export default MetaTags;

export const addSchemaOrg = (text: string) => {
  return (
    <Helmet>
      <script type="application/ld+json">{text}</script>
    </Helmet>
  );
};
